/**
 * Message bubble component with citations
 * Sprint 1, Task 11: Added feedback integration
 */

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { User, Bot } from 'lucide-react';
import { Message } from '../../stores/chat';
import { CitationCard } from './CitationCard';
import { Badge } from '../UI/Badge';
import { FeedbackWidget } from '../Feedback/FeedbackWidget';
import { FeedbackModal } from '../Feedback/FeedbackModal';
import { useWorkspace } from '../../hooks/useWorkspace';

export interface MessageBubbleProps {
  message: Message;
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const isUser = message.role === 'user';
  const { projectId } = useWorkspace();
  const [showDetailedFeedback, setShowDetailedFeedback] = useState(false);

  return (
    <div className={clsx('flex gap-3', isUser ? 'justify-end' : 'justify-start')}>
      {!isUser && (
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <Bot className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
        </div>
      )}

      <div className={clsx('max-w-3xl', isUser && 'order-first')}>
        <div
          className={clsx(
            'px-4 py-3 rounded-lg',
            isUser
              ? 'bg-primary-600 dark:bg-primary-500 text-white'
              : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-gray-100'
          )}
        >
          {message.mode && (
            <Badge variant={message.mode === 'compliance' ? 'info' : 'success'} size="sm" className="mb-2">
              {message.mode}
            </Badge>
          )}
          
          <div className="whitespace-pre-wrap">{message.content}</div>
          
          {message.confidence !== undefined && (
            <div className="mt-2 text-sm opacity-75 dark:opacity-90">
              Confidence: {(message.confidence * 100).toFixed(1)}%
            </div>
          )}
        </div>

        {message.citations && message.citations.length > 0 && (
          <div className="mt-2 space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-300 font-medium">
              {message.citations.length} {message.citations.length === 1 ? 'Source' : 'Sources'}
            </p>
            {message.citations.map((citation) => (
              <CitationCard key={citation.id} citation={citation} />
            ))}
          </div>
        )}

        {/* Feedback widget for assistant messages */}
        {!isUser && projectId && (
          <div className="mt-3">
            <FeedbackWidget
              queryId={message.id}
              projectId={projectId}
              answer={message.content}
              citations={message.citations}
              compact={true}
              onFeedbackSubmit={() => {
                // Optional: Show toast notification
                console.log('[FEEDBACK] Thank you for your feedback!');
              }}
            />
            
            {showDetailedFeedback && (
              <FeedbackModal
                isOpen={showDetailedFeedback}
                onClose={() => setShowDetailedFeedback(false)}
                queryId={message.id}
                projectId={projectId}
                answer={message.content}
                citations={message.citations}
              />
            )}
          </div>
        )}

        <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">
          {message.timestamp.toLocaleTimeString()}
        </div>
      </div>

      {isUser && (
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center">
            <User className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </div>
        </div>
      )}
    </div>
  );
}


